import { usePersonas } from "@/hooks/use-personas";
import { type Configuration } from "@/types/campaign";
import { useBlocker } from "@tanstack/react-router";
import type { SetStateAction } from "jotai";
import { useMemo, useRef, useState, type Dispatch } from "react";
import { twMerge } from "tailwind-merge";
import { SegmentHeader } from "../campaign/basic/SegmentHeader";
import { AlertModal } from "../generic/AlertModal";
import { Button } from "../generic/Button";
import { PersonaForm } from "./PersonaForm";

type ContactWindowProps = {
  configuration: Configuration;
  setConfiguration: Dispatch<SetStateAction<Configuration>>;
  onDoneUpdating: () => void;
};

export function ContactWindow({
  configuration,
  setConfiguration,
  onDoneUpdating,
}: ContactWindowProps) {
  const contactConfiguration = useMemo(
    () => configuration.contactConfiguration,
    [configuration.contactConfiguration],
  );
  const {
    personas,
    addPersona,
    removePersona,
    reorderPersona,
    updatePersona,
    clearPersonas,
  } = usePersonas(contactConfiguration);

  const [editingPersonaIndex, setEditingPersonaIndex] = useState<number | null>(
    null,
  );

  const showEmptyState = personas.length === 0;

  const config = contactConfiguration ?? {
    requireContact: false,
    personas: [],
  };

  const isIntentionalNavRef = useRef(false);

  const isDirty = useMemo(() => {
    const savedPersonas = contactConfiguration?.personas ?? [];

    if (savedPersonas.length === 0 && personas.length === 0) return false;

    if (personas.length !== savedPersonas.length) return true;
    return JSON.stringify(personas) !== JSON.stringify(savedPersonas);
  }, [personas, contactConfiguration?.personas]);

  const blocker = useBlocker({
    shouldBlockFn: () => {
      if (isIntentionalNavRef.current) {
        return false;
      }
      return isDirty;
    },
    withResolver: true,
  });

  const handleSave = () => {
    isIntentionalNavRef.current = true;
    setConfiguration({
      ...configuration,
      contactConfiguration: {
        ...config,
        personas,
      },
    });
    // updateContactConfiguration({
    //   ...config,
    //   personas: personas,
    // });
    onDoneUpdating();
  };

  const handleClear = () => {
    isIntentionalNavRef.current = true;

    clearPersonas();

    setConfiguration({
      ...configuration,
      contactConfiguration: {
        ...config,
        personas: [],
      },
    });
    onDoneUpdating();
  };

  const handleRemovePersona = (index: number) => {
    removePersona(index);

    if (personas.length === 1) {
      setConfiguration({
        ...configuration,
        contactConfiguration: {
          ...config,
          personas: [],
        },
      });
      isIntentionalNavRef.current = true;
      onDoneUpdating();
    }
  };

  return (
    <div className="flex size-full flex-col items-center justify-between">
      <div
        className={twMerge(
          "flex h-full w-full max-w-2xl flex-col items-center gap-y-10",
          showEmptyState && "justify-center",
        )}
      >
        {!showEmptyState && (
          <SegmentHeader
            className="w-122"
            title="Kontaktpersoner"
            description={`Find leads med oplysninger på de personer du ønsker at tale med.\n Du kan tilføje flere personaer, men der er match på et lead hvis blot én af dem findes.`}
          />
        )}

        <div className="w-full space-y-10">
          <PersonaForm
            configuration={configuration}
            personas={personas}
            onAddPersona={addPersona}
            onRemovePersona={handleRemovePersona}
            onReorderPersona={reorderPersona}
            onUpdatePersona={updatePersona}
            editingPersonaIndex={editingPersonaIndex}
            setEditingPersonaIndex={setEditingPersonaIndex}
          />

          {!showEmptyState && (
            <div className="mt-auto flex flex-wrap justify-center gap-4 pt-12">
              <Button
                size="lg"
                type="button"
                intent="secondary"
                animate
                onPress={() => {
                  onDoneUpdating();
                }}
              >
                Tilbage
              </Button>
              <Button
                size="lg"
                type="button"
                animate
                onPress={handleSave}
                isDisabled={personas.length === 0}
              >
                Gem
              </Button>
              {!!contactConfiguration?.personas?.length && (
                <div className="flex w-full justify-center">
                  <Button size="lg" intent="tertiary" onPress={handleClear}>
                    Fjern filter
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={blocker.proceed}
      />
    </div>
  );
}
